import { DOCUMENT, Injectable, inject, signal } from '@angular/core';
import {
  TranslocoService,
  getBrowserCultureLang,
  getBrowserLang,
} from '@jsverse/transloco';
import { ToastService } from '@sankarah/shared-ui';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ProfileService } from '../services/profile.service';
import { normalizeAppLanguage } from './normalize-app-language';
import {
  COMMO_DEFAULT_LANGUAGE,
  type CommoLanguage,
  isCommoLanguage,
} from './supported-languages';

/** E2E で `en` 固定するときも同じキーを使う。 */
export const COMMO_LANGUAGE_STORAGE_KEY = 'commo_preferred_language';
const STORAGE_PROMPT_DONE_KEY = 'commo_language_prompt_done';

@Injectable({ providedIn: 'root' })
export class LanguagePreferenceService {
  private readonly transloco = inject(TranslocoService);
  private readonly auth = inject(AuthService);
  private readonly profiles = inject(ProfileService);
  private readonly toastService = inject(ToastService);
  private readonly document = inject(DOCUMENT);

  private readonly _activeLanguage = signal<CommoLanguage>(
    COMMO_DEFAULT_LANGUAGE,
  );
  readonly activeLanguage = this._activeLanguage.asReadonly();

  /** 起動時: 端末保存 → なければブラウザ検出（不可時は en）。 */
  initFromDevice(): void {
    this.applyLanguage(
      this.readStoredLanguage() ?? this.detectBrowserLanguage(),
    );
  }

  /** ログイン中: アカウント言語を優先。無ければ端末／検出言語を DB に保存。 */
  async syncWithAccount(): Promise<void> {
    await this.auth.waitForSession();
    const user = this.auth.user();
    if (!user || this.auth.isAnonymous()) return;

    const profile = await firstValueFrom(
      this.profiles.getCurrentProfile(user.id),
    );
    const fromAccount = profile?.preferred_language;
    if (isCommoLanguage(fromAccount)) {
      this.applyLanguage(fromAccount);
      return;
    }
    const local = this.readStoredLanguage() ?? this.detectBrowserLanguage();
    this.applyLanguage(local);
    this.persistToAccount(local);
  }

  /** 初回ページで選択をプレビュー（案内完了にはしない）。 */
  previewLanguage(lang: CommoLanguage): void {
    this.applyLanguage(lang, false);
  }

  /** 言語を確定（Profile の ion-select など）。 */
  setLanguage(lang: CommoLanguage): void {
    this.applyLanguage(lang);
    this.markPromptDone();
    this.persistToAccountIfLoggedIn(lang);
  }

  /** 初回ページの Continue: いまの active を確定。 */
  confirmPrompt(): void {
    this.setLanguage(this._activeLanguage());
  }

  /** Continue 済みか。未完了なら `/auth/language` へ。 */
  shouldShowPrompt(): boolean {
    try {
      return localStorage.getItem(STORAGE_PROMPT_DONE_KEY) !== '1';
    } catch {
      return false;
    }
  }

  private detectBrowserLanguage(): CommoLanguage {
    return normalizeAppLanguage(
      getBrowserCultureLang() || getBrowserLang() || COMMO_DEFAULT_LANGUAGE,
    );
  }

  private applyLanguage(lang: CommoLanguage, persistLocal = true): void {
    this._activeLanguage.set(lang);
    this.transloco.setActiveLang(lang);
    this.document.documentElement.lang = lang;
    if (persistLocal) {
      this.writeStoredLanguage(lang);
    }
  }

  private persistToAccountIfLoggedIn(lang: CommoLanguage): void {
    if (!this.auth.isAuthenticated() || this.auth.isAnonymous()) return;
    this.persistToAccount(lang);
  }

  private persistToAccount(lang: CommoLanguage): void {
    this.profiles.upsertPreferredLanguage(lang).subscribe({
      error: () => {
        void this.toastService.showError(
          this.transloco.translate('common.saveFailed'),
        );
      },
    });
  }

  private readStoredLanguage(): CommoLanguage | null {
    try {
      const raw = localStorage.getItem(COMMO_LANGUAGE_STORAGE_KEY);
      return isCommoLanguage(raw) ? raw : null;
    } catch {
      return null;
    }
  }

  private writeStoredLanguage(lang: CommoLanguage): void {
    try {
      localStorage.setItem(COMMO_LANGUAGE_STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
  }

  private markPromptDone(): void {
    try {
      localStorage.setItem(STORAGE_PROMPT_DONE_KEY, '1');
    } catch {
      /* ignore */
    }
  }
}
